/**
 * Adapter-Schicht für Spracherkennung.
 *
 * WARUM eine Abstraktion statt direkter `webkitSpeechRecognition`-Aufrufe im UI:
 * Die Web Speech API ist nur eine von mehreren möglichen Quellen. Eine
 * serverseitige Transkription (z. B. selbst gehostetes Whisper) soll später als
 * zweite Implementierung desselben Interfaces dahinter passen, ohne dass ein
 * einziges UI-Bauteil angefasst werden muss.
 *
 * DATENSCHUTZ — bitte nicht überlesen:
 * Die Web Speech API läuft in Chrome/Edge NICHT lokal. Der Browser überträgt
 * das Audio an den Spracherkennungsdienst des Herstellers (Google bzw.
 * Microsoft). Für eine Anwendung mit Bauherren- und Objektdaten ist das eine
 * bewusste Abwägung, keine Nebensache — deshalb liegt die Sprachbedienung
 * hinter einem Feature-Flag (Standard: aus) und die Oberfläche weist beim
 * Zuhören sichtbar darauf hin. Wer das nicht will, ersetzt den Adapter durch
 * eine eigene Implementierung.
 */

/** Ein Erkennungsergebnis — `final` unterscheidet Zwischen- von Endergebnis. */
export interface SpracherkennungErgebnis {
  text: string;
  final: boolean;
  /** 0..1, vom Erkenner geschätzt. Bei Zwischenergebnissen oft 0. */
  konfidenz: number;
}

/** Fehlerklassen, die die Oberfläche unterschiedlich erklären muss. */
export type SpracherkennungFehler =
  | 'nicht-unterstuetzt'
  | 'zugriff-verweigert'
  | 'kein-mikrofon'
  | 'netzwerk'
  | 'nichts-verstanden'
  | 'sprache-nicht-unterstuetzt'
  | 'abgebrochen'
  | 'unbekannt';

/** Erklärtexte für den Nutzer — kein Fehlercode-Kauderwelsch in der Oberfläche. */
export const FEHLER_TEXTE: Record<SpracherkennungFehler, string> = {
  'nicht-unterstuetzt':
    'Dieser Browser kann keine Spracherkennung. Chrome oder Edge verwenden — Firefox unterstützt sie nicht, Safari nur eingeschränkt.',
  'zugriff-verweigert':
    'Der Zugriff auf das Mikrofon wurde abgelehnt. In der Adressleiste auf das Schloss-Symbol tippen, „Mikrofon“ auf „Zulassen“ setzen und die Seite neu laden.',
  'kein-mikrofon': 'Kein Mikrofon gefunden. Gerät anschließen und erneut versuchen.',
  netzwerk:
    'Die Spracherkennung des Browsers ist nicht erreichbar (sie benötigt eine Internetverbindung).',
  'nichts-verstanden': 'Nichts gehört. Näher ans Mikrofon und noch einmal sprechen.',
  'sprache-nicht-unterstuetzt':
    'Deutsch wird von der Spracherkennung dieses Browsers nicht angeboten.',
  abgebrochen: 'Aufnahme abgebrochen.',
  unbekannt: 'Die Spracherkennung wurde unerwartet beendet.',
};

/** Fehler, nach denen ein automatischer Neustart sinnlos ist. */
export const ENDGUELTIGE_FEHLER: readonly SpracherkennungFehler[] = [
  'nicht-unterstuetzt',
  'zugriff-verweigert',
  'kein-mikrofon',
  'sprache-nicht-unterstuetzt',
];

export interface SpracherkennungOptionen {
  /** BCP-47-Sprachkennung, z. B. 'de-DE'. */
  sprache: string;
  /** Weiterhören statt nach dem ersten Satz zu stoppen. */
  dauerhaft: boolean;
  /** Zwischenergebnisse liefern, damit der Nutzer beim Sprechen mitliest. */
  zwischenergebnisse: boolean;
  onErgebnis: (ergebnis: SpracherkennungErgebnis) => void;
  onFehler: (fehler: SpracherkennungFehler) => void;
  /** Wird immer aufgerufen, wenn die Erkennung endet — auch nach einem Fehler. */
  onEnde: () => void;
}

export interface SpracherkennungSitzung {
  /** Beendet die Aufnahme, liefert aber ein noch offenes Endergebnis nach. */
  stopp: () => void;
  /** Bricht sofort ab und verwirft Offenes (z. B. beim Unmount). */
  abbrechen: () => void;
}

export interface SpracherkennungAdapter {
  readonly name: string;
  verfuegbar: () => boolean;
  starte: (optionen: SpracherkennungOptionen) => SpracherkennungSitzung;
}

/*
 * Minimal-Typen der Web Speech API. Bewusst eigene Namen statt der globalen
 * Deklarationen: `webkitSpeechRecognition` steht in keiner lib.dom, und je nach
 * TypeScript-Version existiert `SpeechRecognition` global oder eben nicht.
 * Eigene Interfaces sind stabil und kommen ohne `any` aus.
 */
interface WebSpeechAlternative {
  readonly transcript: string;
  readonly confidence: number;
}

interface WebSpeechResult {
  readonly isFinal: boolean;
  readonly length: number;
  readonly [index: number]: WebSpeechAlternative;
}

interface WebSpeechResultList {
  readonly length: number;
  readonly [index: number]: WebSpeechResult;
}

interface WebSpeechErgebnisEvent {
  readonly resultIndex: number;
  readonly results: WebSpeechResultList;
}

interface WebSpeechFehlerEvent {
  readonly error: string;
}

interface WebSpeechRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: WebSpeechErgebnisEvent) => void) | null;
  onerror: ((event: WebSpeechFehlerEvent) => void) | null;
  onend: (() => void) | null;
}

type WebSpeechKonstruktor = new () => WebSpeechRecognition;

interface FensterMitSpracherkennung {
  SpeechRecognition?: WebSpeechKonstruktor;
  webkitSpeechRecognition?: WebSpeechKonstruktor;
}

function holeKonstruktor(): WebSpeechKonstruktor | undefined {
  if (typeof window === 'undefined') return undefined;
  const fenster = window as unknown as FensterMitSpracherkennung;
  return fenster.SpeechRecognition ?? fenster.webkitSpeechRecognition;
}

/** Übersetzt die Fehlerkennungen der Web Speech API in unsere Fehlerklassen. */
function uebersetzeFehler(code: string): SpracherkennungFehler {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'zugriff-verweigert';
    case 'audio-capture':
      return 'kein-mikrofon';
    case 'network':
      return 'netzwerk';
    case 'no-speech':
      return 'nichts-verstanden';
    case 'language-not-supported':
    case 'bad-grammar':
      return 'sprache-nicht-unterstuetzt';
    case 'aborted':
      return 'abgebrochen';
    default:
      return 'unbekannt';
  }
}

export const webSpeechAdapter: SpracherkennungAdapter = {
  name: 'web-speech',

  verfuegbar: () => holeKonstruktor() !== undefined,

  starte(optionen) {
    const Konstruktor = holeKonstruktor();
    if (!Konstruktor) {
      // Kein Absturz, sondern derselbe Weg wie jeder andere Fehler — die
      // Oberfläche zeigt eine Erklärung und bleibt bedienbar.
      optionen.onFehler('nicht-unterstuetzt');
      optionen.onEnde();
      return { stopp: () => {}, abbrechen: () => {} };
    }

    const erkennung = new Konstruktor();
    erkennung.lang = optionen.sprache;
    erkennung.continuous = optionen.dauerhaft;
    erkennung.interimResults = optionen.zwischenergebnisse;
    erkennung.maxAlternatives = 1;

    erkennung.onresult = (event) => {
      // Ab `resultIndex` stehen die seit dem letzten Event neuen Ergebnisse.
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const ergebnis = event.results[i];
        const beste = ergebnis?.[0];
        if (!ergebnis || !beste) continue;
        optionen.onErgebnis({
          text: beste.transcript,
          final: ergebnis.isFinal,
          konfidenz: beste.confidence,
        });
      }
    };

    erkennung.onerror = (event) => optionen.onFehler(uebersetzeFehler(event.error));
    erkennung.onend = () => optionen.onEnde();

    try {
      erkennung.start();
    } catch {
      // `start()` wirft, wenn bereits eine Erkennung läuft (InvalidStateError).
      // Für den Aufrufer ist das kein Sonderfall, sondern schlicht ein Ende.
      optionen.onFehler('unbekannt');
      optionen.onEnde();
    }

    return {
      stopp: () => {
        try {
          erkennung.stop();
        } catch {
          /* Bereits beendet — nichts zu tun. */
        }
      },
      abbrechen: () => {
        erkennung.onresult = null;
        erkennung.onerror = null;
        erkennung.onend = null;
        try {
          erkennung.abort();
        } catch {
          /* Bereits beendet — nichts zu tun. */
        }
      },
    };
  },
};

/** Kann dieser Browser überhaupt Spracherkennung? */
export function spracherkennungVerfuegbar(): boolean {
  return webSpeechAdapter.verfuegbar();
}
